import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { RevcallLogo } from '@/components/common/RevcallLogo';
import { BRAND } from '@/config/brand';

/**
 * Gabarit des pages légales (W1 brick, B3-confidential / B3-cgu) : la marque
 * avec retour à l'accueil, le titre et la date, un sommaire ancré, puis les
 * sections. Le texte reste celui des pages ; seul le cadre est commun.
 */
export interface LegalSectionRef { id: string; heading: string }

export function LegalSection({ id, heading, children }: { id: string; heading: string; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-h`} className="scroll-mt-6">
      <h2 id={`${id}-h`} className="text-[16px] font-semibold" style={{ color: 'var(--vf-text-1)' }}>{heading}</h2>
      <div className="mt-2 flex flex-col gap-3">{children}</div>
    </section>
  );
}

interface Props {
  title: string;
  lastUpdated: string;
  sections: LegalSectionRef[];
  /** The other legal page, linked at the bottom. */
  related: { to: string; label: string };
  children: ReactNode;
}

export function LegalPage({ title, lastUpdated, sections, related, children }: Props) {
  const { t } = useTranslation(['pages']);
  return (
    <div className="min-h-screen px-4 py-8 sm:px-6 lg:px-8" style={{ background: 'var(--vf-surface-1)', color: 'var(--vf-text-1)' }}>
      <div className="mx-auto max-w-5xl">
        <header className="mb-6 flex items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-2 text-[14px] font-bold" aria-label={t('pages:legal.common.home', { brand: BRAND.name })}>
            <RevcallLogo size={18} color="var(--vf-accent)" />
            {BRAND.name}
          </Link>
          <Link to="/" className="inline-flex min-h-[40px] items-center gap-1.5 text-[13px] font-medium" style={{ color: 'var(--vf-accent-text)' }}>
            <ArrowLeft size={14} aria-hidden />
            {t('pages:legal.common.back')}
          </Link>
        </header>
        <div className="grid gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
          <nav aria-label={t('pages:legal.common.toc')} className="lg:sticky lg:top-6 lg:self-start">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--vf-text-3)' }}>{t('pages:legal.common.toc')}</p>
            <ol className="flex flex-col gap-1 text-[13px]">
              {sections.map((s) => (
                <li key={s.id}><a href={`#${s.id}`} className="block rounded px-2 py-1 hover:underline" style={{ color: 'var(--vf-text-2)' }}>{s.heading}</a></li>
              ))}
            </ol>
          </nav>
          <article className="min-w-0 rounded-xl border p-6 sm:p-10" style={{ background: 'var(--vf-bg)', borderColor: 'var(--vf-border)' }}>
            <h1 className="text-[28px] font-bold leading-tight">{title}</h1>
            <p className="mb-8 mt-1 text-[13px]" style={{ color: 'var(--vf-text-3)' }}>{lastUpdated}</p>
            <div className="flex flex-col gap-8 text-[14px] leading-relaxed [&_a]:underline [&_ul]:ml-6 [&_ul]:list-disc [&_ul]:space-y-1" style={{ color: 'var(--vf-text-2)' }}>
              {children}
            </div>
            <div className="mt-12 flex items-center justify-between border-t pt-6 text-[13px]" style={{ borderColor: 'var(--vf-border)' }}>
              <Link to="/" style={{ color: 'var(--vf-accent-text)' }}>{t('pages:legal.common.backHome')}</Link>
              <Link to={related.to} style={{ color: 'var(--vf-accent-text)' }}>{related.label}</Link>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}

export default LegalPage;
